import React, { useEffect, useMemo, useState } from "react";
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from "react-router-dom";

import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";

import { auth, db } from "./firebase";

// Pages
import EmployeePortal from "./pages/EmployeePortal";
import AdminHome from "./pages/AdminHome";
import Reports from "./pages/Reports";

// UI
import NotificationBell from "./components/NotificationBell";

// -------------------- role + profile helpers --------------------
const HARD_ADMIN_EMAILS = new Set([
  "benjamin@mcins.ca", // always admin
]);

const ROLE_RANK = {
  employee: 0,
  admin: 1,
  developer: 2,
};

function toEmailLower(email) {
  return String(email || "").trim().toLowerCase();
}

function normalizeRoleString(role) {
  const r = String(role || "").trim().toLowerCase();
  if (r === "developer") return "developer";
  if (r === "admin") return "admin";
  return "employee";
}

/**
 * Supports legacy/alternate shapes:
 * - role: "admin" | "developer" | "employee"
 * - isAdmin: true / admin: true
 * - isDeveloper: true / developer: true
 * - roles: ["admin", ...]
 */
function inferRoleFromDocData(data) {
  if (!data || typeof data !== "object") return "employee";

  // roles array
  if (Array.isArray(data.roles)) {
    const set = new Set(data.roles.map((x) => String(x || "").toLowerCase()));
    if (set.has("developer")) return "developer";
    if (set.has("admin")) return "admin";
  }

  // explicit booleans
  if (data.isDeveloper === true || data.developer === true) return "developer";
  if (data.isAdmin === true || data.admin === true) return "admin";

  // string role
  if (typeof data.role === "string") return normalizeRoleString(data.role);

  return "employee";
}

function pickHighestRole(roles) {
  let best = "employee";
  let bestRank = ROLE_RANK.employee;

  for (const r of roles) {
    const rr = normalizeRoleString(r);
    const rank = ROLE_RANK[rr] ?? 0;
    if (rank > bestRank) {
      best = rr;
      bestRank = rank;
    }
  }
  return best;
}

function effectiveRole(emailLower, inferredRoles) {
  if (HARD_ADMIN_EMAILS.has(String(emailLower || "").toLowerCase())) return "admin";
  return pickHighestRole(inferredRoles);
}

async function readAllUserDocCandidates({ uid, email, emailLower }) {
  const candidates = [
    { id: uid, kind: "uid" },
    ...(emailLower ? [{ id: emailLower, kind: "emailLower" }] : []),
    ...(email ? [{ id: email, kind: "email" }] : []),
  ];

  const found = [];

  for (const c of candidates) {
    try {
      const ref = doc(db, "users", c.id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        found.push({ id: c.id, kind: c.kind, data: snap.data() });
      }
    } catch (e) {
      console.warn("users/ read failed for", c, e?.code, e?.message);
    }
  }

  return found;
}

function mergeCandidateData(baseProfile, candidates) {
  // Merge order: legacy first, uid last (uid gets final say on normal fields),
  // but we will override role separately using “highest privilege wins”.
  const order = { email: 0, emailLower: 1, uid: 2 };
  const sorted = [...candidates].sort((a, b) => (order[a.kind] ?? 99) - (order[b.kind] ?? 99));

  return sorted.reduce((acc, c) => ({ ...acc, ...(c.data || {}) }), { ...baseProfile });
}

export default function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("employee");
  const [loading, setLoading] = useState(true);

  const isPrivileged = role === "admin" || role === "developer";

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      try {
        if (!u) {
          setUser(null);
          setRole("employee");
          setLoading(false);
          return;
        }

        await fetchOrCreateUser(u);
        setLoading(false);
      } catch (e) {
        console.error("Auth/profile load failed:", e);
        setUser(null);
        setRole("employee");
        setLoading(false);
      }
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchOrCreateUser(u) {
    const uid = u.uid;
    const email = u.email || "";
    const emailLower = toEmailLower(email);

    const baseProfile = {
      email,
      emailLower,
      displayName: u.displayName || "",
      role: "employee",
      annualAllotmentDays: 15,
      workFromHomeAllotmentDays: 10,
    };

    // Read *all* possible docs so we don’t get trapped by an outdated users/{uid}
    const candidates = await readAllUserDocCandidates({ uid, email, emailLower });
    const merged = mergeCandidateData(baseProfile, candidates);

    // Determine best role across all candidate docs + hard allowlist
    const inferredRoles = [
      merged.role,
      ...candidates.map((c) => inferRoleFromDocData(c.data)),
    ];
    const finalRole = effectiveRole(emailLower, inferredRoles);

    const uidRef = doc(db, "users", uid);
    const uidSnap = await getDoc(uidRef);

    // Write back to users/{uid} so future logins are stable and consistent
    const writeData = {
      ...merged,
      email,
      emailLower,
      displayName: u.displayName || merged.displayName || "",
      role: finalRole,
      updatedAt: serverTimestamp(),
    };

    if (!uidSnap.exists()) {
      await setDoc(
        uidRef,
        {
          ...writeData,
          createdAt: serverTimestamp(),
          migratedFrom:
            candidates.length > 0
              ? candidates.map((c) => ({ kind: c.kind, id: c.id }))
              : null,
        },
        { merge: true }
      );
    } else {
      await setDoc(uidRef, writeData, { merge: true });
    }

    setUser({ uid, ...merged, role: finalRole });
    setRole(finalRole);
  }

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error("Logout error:", e);
    }
  };

  const displayName = useMemo(() => {
    if (!user) return "";
    return user.displayName || user.email || "";
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600">
        Loading…
      </div>
    );
  }

  // If not logged in, show EmployeePortal which already contains auth UI
  if (!user) {
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<EmployeePortal user={null} />} />
          <Route path="/" element={<EmployeePortal user={null} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        {/* Top Bar */}
        <div className="bg-blue-700 text-white">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="font-semibold">Time Off Tracker</div>

              <nav className="flex items-center gap-3 text-sm">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `px-3 py-1 rounded ${isActive ? "bg-blue-600" : "hover:bg-blue-600/60"}`
                  }
                >
                  Employee
                </NavLink>

                {isPrivileged && (
                  <>
                    <NavLink
                      to="/admin"
                      className={({ isActive }) =>
                        `px-3 py-1 rounded ${isActive ? "bg-blue-600" : "hover:bg-blue-600/60"}`
                      }
                    >
                      Admin
                    </NavLink>
                    <NavLink
                      to="/reports"
                      className={({ isActive }) =>
                        `px-3 py-1 rounded ${isActive ? "bg-blue-600" : "hover:bg-blue-600/60"}`
                      }
                    >
                      Reports
                    </NavLink>
                  </>
                )}
              </nav>
            </div>

            <div className="flex items-center gap-3">
              <NotificationBell user={user} />
              <div className="text-xs opacity-90 hidden sm:block">
                {displayName}
                {isPrivileged ? ` (${role})` : ""}
              </div>
              <button
                onClick={handleLogout}
                className="bg-blue-600 hover:bg-blue-500 px-3 py-1 rounded text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Main */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Routes>
            <Route path="/" element={<EmployeePortal user={user} />} />

            <Route
              path="/admin"
              element={isPrivileged ? <AdminHome user={user} /> : <Navigate to="/" replace />}
            />

            <Route
              path="/reports"
              element={isPrivileged ? <Reports user={user} /> : <Navigate to="/" replace />}
            />

            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </Router>
  );
}
